import * as THREE from "three";
import { CHUNK, crosserX, roadLight, sideLight, trafficZ, type CourseChunk, type HillCrosser, type HillCrossing, type HillJunction, type HillTraffic, type LightColor } from "../hill-course";
import { chunkDressing, ROADSIDE } from "../hill-dressing";
import { crossingBusy, SHOPFRONT } from "../hill-walk";
import type { HillRoad } from "../hill-road";
import { stopLineZ } from "../hill-traffic";
import { rampPrims } from "../hill-ramps";
import type { MapLook } from "../maps";
import { HILL_FINISH } from "../sim/tuning";
import { buildPieces, disposeWorld } from "./level-meshes";
import { mergeStatic } from "./merge";
import { PedestrianMeshes, type PeopleRider } from "./pedestrians";
import { addStillScenery } from "./scenery";
import { hillKit, kitMaterials, type Kit } from "./hill-kit";
import { grindRail, obstacle, vehicle } from "./hill-obstacles";

// Bomb the Hill's obstacles (#194), drawn: parked cars, vans and buses,
// cones, bins, barriers, rails, potholes and gravel, the rails to grind (#237) (built in
// `hill-obstacles.ts` from `hill-kit.ts`'s materials, #217), the road's ramps and
// the traffic lane's dashes, all built flat (y up from the road) and
// batched a chunk of road at a time (#195), in a group sheared down the hill
// the way the park is (`buildLevelMeshes`). The traffic moves with the
// sim's clock each frame. A vehicle's nose is local +z, down the hill.
// Side streets (#197) are drawn with their chunk: the road's side walls
// (broken where they cross), the side street's asphalt, kerbs, centre line
// and stop lines (the traffic lane's too), and a traffic light on each
// corner showing its cycle (#214): one lamp up the road, one out along the
// side street. The cross traffic drives with the clock, headlights on. The roadside (#211: buildings, lamps and trees, `hill-dressing.ts`)
// and the white edge lines are drawn with their chunk too, so they move with
// the road and leave every side street clear, its corners kerbed round. The
// roadside is batched into its chunk's batch with one set of materials for
// every chunk, each light's lamps are batched, and each moving vehicle is
// merged into a mesh per material (#216);
// `hill-digest.test.ts` pins what all of this draws.

/** The most meshes (outlines too) a chunk of road may draw (#216): its batch, ramps, cross traffic and lights. */
export const CHUNK_MESHES = 110;

/** The most meshes (outlines too) a moving vehicle may draw (#216): one per material, outlines apart (a car body's two weights of ink). */
export const VEHICLE_MESHES = 10;

/** How far past the road's walls a side street's traffic is drawn (m): it loops round out of sight past that. */
const JUNCTION_DRAWN = 36;

/** One chunk of the road (#195): everything on it that stands still, its ramps and the traffic lane's dashes, in course z, batched. */
function buildChunk(k: Kit, road: HillRoad, chunk: CourseChunk, look: MapLook): THREE.Group {
  const root = new THREE.Group();
  for (const o of chunk.obstacles) {
    const m = obstacle(k, o);
    m.position.set(o.x, m.position.y, o.z);
    root.add(m);
  }
  // The rails to grind (#237), on down the hill from their top ends.
  for (const g of chunk.grinds) {
    const m = grindRail(k, g);
    m.position.set(g.x, 0, g.z);
    root.add(m);
  }
  // The dashed line down the traffic lane's edge, every 9 m of course z so chunks line up.
  const edge = road.course.lane.x0 > 0 ? road.course.lane.x0 : road.course.lane.x1;
  const dash = new THREE.BoxGeometry(0.15, 0.02, 3);
  const from = road.top + chunk.k * CHUNK;
  for (let z = Math.ceil(from / 9) * 9; z < from + CHUNK; z += 9) {
    const d = new THREE.Mesh(dash, k.paint);
    d.position.set(edge, 0.01, z);
    d.receiveShadow = true;
    root.add(d);
  }
  // Zebra crossings (#196): white bars across the road, each running along it.
  const bar = new THREE.BoxGeometry(0.5, 0.02, 1);
  for (const c of chunk.crossings) {
    const W = road.halfWidth;
    for (let x = -W + 0.45; x < W - 0.2; x += 1) {
      const b = new THREE.Mesh(bar, k.paint);
      b.scale.z = c.hl * 2;
      b.position.set(x, 0.011, c.z);
      b.receiveShadow = true;
      root.add(b);
    }
  }
  buildWalls(k, road, chunk, root);
  for (const c of chunk.crossings) buildCrossingSides(k, road.halfWidth, c, root);
  for (const j of chunk.junctions) buildSideStreet(k, road.halfWidth, road.course.lane, j, root);
  // The roadside (#211), in the scenery's own look, batched with the rest (#216).
  const side = chunkDressing(
    road.top,
    chunk.k,
    road.chunks.flatMap((c) => c.junctions),
    [...road.chunks, ...(road.next ? [road.next] : [])].flatMap((c) => c.crossings),
  ).map((d) => ({ type: d.kind, x: d.x, z: d.z, rot: d.kind === "building" && d.side > 0 ? Math.PI : 0, size: d.kind === "tree" ? d.size : 1, ...(d.kind === "building" ? { len: 12, wide: 9, h: d.size, color: d.color } : {}) }));
  addStillScenery(side, root, k.scenery);
  const merged = mergeStatic(root);
  merged.name = `hill-chunk-${chunk.k}`;
  // The ramps are drawn the way the park's kickers are, not batched.
  const ramps = buildPieces(rampPrims(chunk.ramps), look);
  if (ramps.length) merged.add(...ramps);
  return merged;
}

/** How far either side of a crosswalk's middle its dropped kerb runs (#202): the wall's broken there. */
const droppedKerb = (c: HillCrossing) => c.hl + 0.3;

/**
 * Where a crosswalk leads (#202), either side of the road: the wall dropped
 * to a kerb across it, a stretch of pavement behind the wall, a footpath
 * straight off the pavement out to a shopfront, its door lit, and a path
 * from each end of the pavement to a lit side door (the people's routes,
 * `hill-walk.ts`; the roadside keeps clear of all of it).
 */
function buildCrossingSides(k: Kit, W: number, c: HillCrossing, root: THREE.Group): void {
  const pave = ROADSIDE.pavement;
  const along = 2 * (c.hl + 6);
  const reach = SHOPFRONT.front;
  const { front, deep, h } = SHOPFRONT;
  const wide = 2 * (c.hl + SHOPFRONT.reach);
  for (const side of [1, -1] as const) {
    const shop = new THREE.Mesh(new THREE.BoxGeometry(deep, h, wide), k.shop);
    shop.position.set(side * (W + front + deep / 2), h / 2, c.z);
    shop.castShadow = true;
    shop.receiveShadow = true;
    root.add(shop);
    // A door at the footpath's end and one at each end of the pavement, lit, under an awning, and a path out to each side door.
    for (const dz of [0, -(c.hl + SHOPFRONT.side), c.hl + SHOPFRONT.side]) {
      const door = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 2.2), k.door);
      door.position.set(side * (W + front - 0.01), 1.1, c.z + dz);
      door.rotation.y = -side * (Math.PI / 2);
      const awning = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.12, 2), k.awning);
      awning.position.set(side * (W + front - 0.45), 2.6, c.z + dz);
      awning.castShadow = true;
      root.add(door, awning);
      if (dz === 0) continue;
      const len = front - 0.5 - pave;
      const walk = new THREE.Mesh(new THREE.BoxGeometry(len, 0.03, 1.4), k.pavement);
      walk.position.set(side * (W + 0.5 + pave + len / 2), 0.015, c.z + dz);
      walk.receiveShadow = true;
      root.add(walk);
    }
    // The walk light's post at the kerb, facing across the road (its lamp's in `buildWalkLights`).
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 2.3, 8), k.pole);
    post.position.set(side * (W + 0.8), 1.15, c.z - droppedKerb(c) - 0.3);
    post.castShadow = true;
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.42, 0.3), k.pole);
    head.position.set(side * (W + 0.8), 2.2, c.z - droppedKerb(c) - 0.3);
    root.add(post, head);
    const kerb = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.12, 2 * droppedKerb(c)), k.pavement);
    kerb.position.set(side * (W + 0.25), 0.06, c.z);
    kerb.receiveShadow = true;
    const slab = new THREE.Mesh(new THREE.BoxGeometry(pave, 0.04, along), k.pavement);
    slab.position.set(side * (W + 0.5 + pave / 2), 0.02, c.z);
    slab.receiveShadow = true;
    const path = new THREE.Mesh(new THREE.BoxGeometry(reach - pave - 0.5, 0.03, 2 * c.hl), k.pavement);
    path.position.set(side * (W + 0.5 + pave + (reach - pave - 0.5) / 2), 0.015, c.z);
    path.receiveShadow = true;
    root.add(kerb, slab, path);
  }
}

/** The road's side walls and white edge lines along a chunk (#197, #211), in course z: broken where any side street crosses, round its kerbed corners, and at each crosswalk's dropped kerb (#202). */
function buildWalls(k: Kit, road: HillRoad, chunk: CourseChunk, root: THREE.Group): void {
  const W = road.halfWidth;
  const wh = 0.8;
  // The first chunk's walls reach on up past the top, where the park's wall was.
  const from = road.top + chunk.k * CHUNK - (chunk.k === 0 ? ROADSIDE.above : 0);
  const to = road.top + (chunk.k + 1) * CHUNK;
  const cuts = road.chunks
    .flatMap((c) => c.junctions)
    .map((j) => [j.z - j.hl - ROADSIDE.corner, j.z + j.hl + ROADSIDE.corner] as const)
    .concat(road.chunks.flatMap((c) => c.crossings).map((c) => [c.z - droppedKerb(c), c.z + droppedKerb(c)] as const))
    .filter(([a, b]) => b > from && a < to)
    .sort((p, q) => p[0] - q[0]);
  const runs: [number, number][] = [];
  let z = from;
  for (const [a, b] of cuts) {
    if (a > z) runs.push([z, a]);
    z = Math.max(z, b);
  }
  if (to > z) runs.push([z, to]);
  for (const [a, b] of runs) {
    for (const side of [1, -1]) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(0.5, wh, b - a), k.wall[side > 0 ? 0 : 1]!);
      wall.position.set(side * (W + 0.25), wh / 2, (a + b) / 2);
      wall.receiveShadow = true;
      wall.castShadow = true;
      // The edge line just inside it.
      const line = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.02, b - a), k.paint);
      line.position.set(side * (W - 0.4), 0.012, (a + b) / 2);
      line.receiveShadow = true;
      root.add(wall, line);
    }
  }
}

/** A side street (#197): its asphalt off both sides of the road, kerbs, a dashed centre line, a stop line before each lane meets the road and across the traffic lane (#214), and the traffic lights' poles. */
function buildSideStreet(k: Kit, W: number, lane: { x0: number; x1: number }, j: HillJunction, root: THREE.Group): void {
  const line = new THREE.Mesh(new THREE.BoxGeometry(lane.x1 - lane.x0 - 0.3, 0.02, 0.4), k.paint);
  line.position.set((lane.x0 + lane.x1) / 2, 0.012, stopLineZ(j) + 0.2);
  root.add(line);
  for (const side of [1, -1] as const) {
    const tar = new THREE.Mesh(new THREE.PlaneGeometry(j.street, j.hl * 2).rotateX(-Math.PI / 2), k.asphalt);
    tar.position.set(side * (W + j.street / 2), 0.004, j.z);
    tar.receiveShadow = true;
    root.add(tar);
    // Its kerbs, from where each corner's rounded off (#211) out to its far end.
    const R = ROADSIDE.corner;
    for (const edge of [-1, 1]) {
      const kerb = new THREE.Mesh(new THREE.BoxGeometry(j.street - R, 0.12, 0.25), k.paint);
      kerb.position.set(side * (W + R + (j.street - R) / 2), 0.06, j.z + edge * (j.hl + 0.125));
      root.add(kerb);
      root.add(cornerKerb(k, side, edge, W, j));
    }
    for (let x = W + 1.5; x < W + j.street; x += 3) {
      const dash = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.02, 0.15), k.paint);
      dash.position.set(side * (x + 0.75), 0.012, j.z);
      root.add(dash);
    }
    // The lane coming in from this side stops at its line: the -x lane (up the hill) comes from +x, the +x lane from -x.
    const stop = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.02, j.hl - 0.2), k.paint);
    stop.position.set(side * (W + 1.4), 0.012, j.z - side * (j.hl / 2));
    root.add(stop);
  }
  // A pole on each corner, its arm out over the road.
  for (const [sx, sz] of CORNERS) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 3.4, 8), k.pole);
    pole.position.set(sx * (W + 0.6), 1.7, j.z + sz * (j.hl + 0.6));
    pole.castShadow = true;
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.95, 0.3), k.pole);
    head.position.set(sx * (W + 0.6), 3.0, j.z + sz * (j.hl + 0.6));
    root.add(pole, head);
    // The unlit lamps behind the lit ones, up the road and out along the side street: red, amber, green.
    for (const dy of Object.values(LAMP_Y)) {
      const up = new THREE.Mesh(new THREE.CircleGeometry(0.12, 12), k.lampOff);
      up.position.set(sx * (W + 0.6), 3.0 + dy, j.z + sz * (j.hl + 0.6) - 0.155);
      up.rotation.y = Math.PI;
      const out = new THREE.Mesh(new THREE.CircleGeometry(0.12, 12), k.lampOff);
      out.position.set(sx * (W + 0.6) + sx * 0.175, 3.0 + dy, j.z + sz * (j.hl + 0.6));
      out.rotation.y = (sx * Math.PI) / 2;
      root.add(up, out);
    }
  }
}

/**
 * A side street's corner rounded off (#211): the tarmac filling the corner
 * between the road's wall line and the side street's kerb, and the kerb
 * round it, a quarter circle `ROADSIDE.corner` across, its centre out on
 * the pavement. `side` is which side of the road, `edge` which side of the
 * side street.
 */
function cornerKerb(k: Kit, side: 1 | -1, edge: number, W: number, j: HillJunction): THREE.Group {
  const R = ROADSIDE.corner;
  const g = new THREE.Group();
  // Worked out for the +x, +z corner (x out from the wall line, z out from the side street's edge), then mirrored point by point.
  const at = (x: number, z: number) => new THREE.Vector2(side * x, -edge * z);
  const n = 6;
  const arc = Array.from({ length: n + 1 }, (_, i) => -Math.PI / 2 - (i / n) * (Math.PI / 2));
  const fill = new THREE.Shape([at(0, 0), at(R, 0), ...arc.map((a) => at(R + R * Math.cos(a), R + R * Math.sin(a)))]);
  const tar = new THREE.Mesh(new THREE.ShapeGeometry(fill).rotateX(-Math.PI / 2), k.asphalt);
  tar.position.y = 0.004;
  tar.receiveShadow = true;
  g.add(tar);
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 - ((i + 0.5) / n) * (Math.PI / 2);
    const seg = new THREE.Mesh(new THREE.BoxGeometry(0.25, 0.12, (R - 0.125) * (Math.PI / 2 / n) + 0.04), k.paint);
    seg.position.set(side * (R + (R - 0.125) * Math.cos(a)), 0.06, edge * (R + (R - 0.125) * Math.sin(a)));
    seg.rotation.y = Math.atan2(-side * Math.sin(a), edge * Math.cos(a));
    g.add(seg);
  }
  g.position.set(side * W, 0, j.z + edge * j.hl);
  return g;
}

/** A side street's four corners (#197), as which side of the road (x) and of the side street (z). */
const CORNERS = [
  [1, -1],
  [-1, -1],
  [1, 1],
  [-1, 1],
] as const;

/** Where each colour's lamp is on a light's head (m, up from its middle): red at the top, green at the bottom (#214). */
const LAMP_Y: Readonly<Record<LightColor, number>> = { red: 0.3, amber: 0, green: -0.3 };

/**
 * A side street's lit lamps (#197, #214), one on each corner's head facing
 * up the road at the rider and the road's traffic (`road`), and one facing
 * out along the side street at its traffic (`side`), each batched. Every
 * frame each set takes its colour and moves to that colour's place on the
 * head (`showLight`).
 */
function buildLights(k: Kit, W: number, j: HillJunction): { road: THREE.Mesh; side: THREE.Mesh } {
  const make = (out: boolean) => {
    const g = new THREE.Group();
    // Its own lamp shape: batching the group frees it.
    const lamp = new THREE.CircleGeometry(0.11, 12);
    for (const [sx, sz] of CORNERS) {
      const x = sx * (W + 0.6);
      const z = j.z + sz * (j.hl + 0.6);
      const m = new THREE.Mesh(lamp, k.red);
      if (out) {
        m.position.set(x + sx * 0.18, 3.0, z);
        m.rotation.y = (sx * Math.PI) / 2;
      } else {
        m.position.set(x, 3.0, z - 0.16);
        m.rotation.y = Math.PI;
      }
      g.add(m);
    }
    return mergeStatic(g).children[0] as THREE.Mesh;
  };
  return { road: make(false), side: make(true) };
}

/**
 * A crosswalk's walk lights (#202), lit while anyone's out on it: on each
 * kerb's post, one face across the road at the far kerb and one up the
 * road at the rider, batched.
 */
function buildWalkLights(k: Kit, W: number, c: HillCrossing): THREE.Mesh {
  const g = new THREE.Group();
  const face = new THREE.PlaneGeometry(0.2, 0.28);
  for (const side of [1, -1] as const) {
    const x = side * (W + 0.8);
    const z = c.z - droppedKerb(c) - 0.3;
    const across = new THREE.Mesh(face, k.walk);
    across.position.set(x - side * 0.155, 2.2, z);
    across.rotation.y = -side * (Math.PI / 2);
    const up = new THREE.Mesh(face, k.walk);
    up.position.set(x, 2.2, z - 0.155);
    up.rotation.y = Math.PI;
    g.add(across, up);
  }
  return mergeStatic(g).children[0] as THREE.Mesh;
}

/**
 * A moving car, van or bus (the traffic lane's and the cross traffic's,
 * #216): `vehicle`'s parts merged into one mesh per material, so it draws
 * as a few meshes instead of ~12. It moves as one, so batching its own
 * parts is all the merge it can take.
 */
function movingVehicle(...args: Parameters<typeof vehicle>): THREE.Group {
  return mergeStatic(vehicle(...args));
}

/** Lights a side street's lamps (`buildLights`) `color` (#214). */
function showLight(k: Kit, lamps: THREE.Mesh, color: LightColor): void {
  lamps.material = k[color];
  lamps.position.y = LAMP_Y[color];
}

/**
 * The hill's road (#194, #195), sheared down the hill with the park: a
 * group per chunk of the course, moved back up as the road moves and built
 * or dropped as chunks are laid out or left behind, and the traffic, moved
 * by the sim's clock each frame.
 */
export class HillRoadMeshes {
  readonly root = new THREE.Group();
  /** The road and what's on it, sheared down the hill. */
  private road = new THREE.Group();
  /** The people on its crosswalks (#196), standing up straight. */
  private people: PedestrianMeshes;
  private kit: Kit;
  private chunks = new Map<number, THREE.Group>();
  private cars = new Map<HillTraffic, THREE.Group>();
  /** The side streets' traffic (#197) and lights, by the chunk they're in. */
  private crossers = new Map<number, { car: HillCrosser; mesh: THREE.Group }[]>();
  private lights = new Map<number, { j: HillJunction; road: THREE.Mesh; side: THREE.Mesh }[]>();
  /** The crosswalks' walk lights (#202), by the chunk they're in. */
  private walks = new Map<number, { c: HillCrossing; lamps: THREE.Mesh }[]>();
  /** The road's run (its seed) and how far it had moved the last time it was synced. */
  private seed = -1;
  private offset = Number.NaN;

  constructor(
    private hill: HillRoad,
    grade: number,
    private look: MapLook,
  ) {
    this.root.name = "hill-road";
    this.kit = hillKit(look);
    // The same shear as the park's (`buildLevelMeshes`): y falls by the grade per metre of z.
    this.road.name = "hill-road-sheared";
    this.road.matrixAutoUpdate = false;
    this.road.matrix.set(1, 0, 0, 0, 0, 1, -grade, 0, 0, 0, 1, 0, 0, 0, 0, 1);
    this.people = new PedestrianMeshes(grade);
    this.root.add(this.road, this.people.root);
  }

  /**
   * Catches up with the road (a new run, or moved on) and puts every vehicle
   * and person crossing (#196) where the sim has them at `clock`, the
   * people reacting to `rider`.
   */
  update(clock: number, rider: PeopleRider | null = null): void {
    const road = this.hill;
    if (road.seed !== this.seed) {
      // A new run is a new road.
      for (const g of this.chunks.values()) this.drop(g);
      for (const g of this.cars.values()) this.drop(g);
      for (const k of [...this.crossers.keys()]) this.dropCrossers(k);
      this.chunks.clear();
      this.cars.clear();
      this.seed = road.seed;
      this.offset = Number.NaN;
    }
    if (road.offset !== this.offset) {
      this.offset = road.offset;
      const keep = new Set(road.chunks.map((c) => c.k));
      for (const [k, g] of this.chunks) {
        if (keep.has(k)) continue;
        this.drop(g);
        this.chunks.delete(k);
        this.dropCrossers(k);
      }
      for (const c of road.chunks) {
        let g = this.chunks.get(c.k);
        if (!g) {
          g = buildChunk(this.kit, road, c, this.look);
          this.chunks.set(c.k, g);
          this.road.add(g);
          const chunk = g;
          const cars = c.crossers.map((car) => {
            const mesh = movingVehicle(this.kit, car.kind, car, car.tint, true);
            mesh.rotation.y = (car.dir * Math.PI) / 2;
            mesh.name = "hill-crosser";
            chunk.add(mesh);
            return { car, mesh };
          });
          this.crossers.set(c.k, cars);
          const lights = c.junctions.map((j) => ({ j, ...buildLights(this.kit, road.halfWidth, j) }));
          for (const l of lights) g.add(l.road, l.side);
          this.lights.set(c.k, lights);
          const walks = c.crossings.map((x) => ({ c: x, lamps: buildWalkLights(this.kit, road.halfWidth, x) }));
          for (const w of walks) g.add(w.lamps);
          this.walks.set(c.k, walks);
        }
        g.position.z = -road.offset;
      }
      const cars = new Set(road.course.traffic);
      for (const [v, g] of this.cars) {
        if (cars.has(v)) continue;
        this.drop(g);
        this.cars.delete(v);
      }
      for (const v of road.course.traffic) {
        if (this.cars.has(v)) continue;
        const g = movingVehicle(this.kit, v.kind, v, v.tint);
        g.name = "hill-traffic";
        this.cars.set(v, g);
        this.road.add(g);
      }
    }
    for (const [v, g] of this.cars) {
      const z = trafficZ(road.course, v, clock);
      // Past the end of the park there's no road to drive on: out of sight in the fog anyway.
      g.visible = z < road.bottom + HILL_FINISH;
      g.position.set(v.x, 0, z);
    }
    // The side streets' traffic (#197) in each chunk (its group's at the road's offset already), and their lights.
    const W = road.halfWidth;
    for (const cars of this.crossers.values()) {
      for (const { car, mesh } of cars) {
        const x = crosserX(road.course, car, clock);
        // Off past the side street's far end it's out of sight (it loops round there).
        mesh.visible = Math.abs(x) < W + JUNCTION_DRAWN;
        mesh.position.set(x, 0, car.z);
      }
    }
    const t = clock - road.course.t0;
    for (const lights of this.lights.values()) {
      for (const l of lights) {
        showLight(this.kit, l.road, roadLight(l.j.light, t));
        showLight(this.kit, l.side, sideLight(l.j.light, t));
      }
    }
    for (const walks of this.walks.values()) {
      for (const w of walks) w.lamps.material = crossingBusy(road.course, w.c.z - road.offset, w.c.hl, clock) ? this.kit.walk : this.kit.lampOff;
    }
    this.people.update(road.course, clock, rider);
  }

  /** Forgets a chunk's side street traffic and lights (#197): they're in its group, freed with it. */
  private dropCrossers(k: number): void {
    this.crossers.delete(k);
    this.lights.delete(k);
    this.walks.delete(k);
  }

  private drop(g: THREE.Object3D): void {
    this.road.remove(g);
    // The kit's materials are shared by every chunk and car: free everything else (geometry, a ramp's materials).
    const shared = new Set(kitMaterials(this.kit));
    g.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      o.geometry.dispose();
      if (o.name === "outline") return;
      // A ramp's mesh can carry a material per face group.
      for (const m of Array.isArray(o.material) ? (o.material as THREE.Material[]) : [o.material as THREE.Material]) {
        if (shared.has(m)) continue;
        if (m instanceof THREE.MeshStandardMaterial) for (const t of [m.map, m.normalMap, m.roughnessMap]) t?.dispose();
        m.dispose();
      }
    });
  }

  dispose(): void {
    this.people.dispose();
    this.root.remove(this.people.root);
    disposeWorld(this.root);
    for (const m of kitMaterials(this.kit)) m.dispose();
  }
}
